import { DOCUMENT } from '@angular/common';
import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterOutlet,
} from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { User } from '@supabase/supabase-js';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { FooterComponent } from './components/layout/footer.component';
import { HeaderComponent } from './components/layout/header.component';
import { LayoutComponent } from './components/layout/layout.component';

import { CookieConsentComponent } from './components/shared/cookie-consent/cookie-consent.component';
import { SnackbarHostComponent } from './components/shared/snackbar-host/snackbar-host.component';
import { NotificationToastComponent } from './components/notifications/notification-toast.component';
import { PushPromptComponent } from './components/notifications/push-prompt.component';
import { AnalyticsService } from './services/analytics.service';
import { DeepLinkService } from './services/deep-link.service';
import { NotificationsService } from './services/notifications.service';
import { SupabaseService } from './services/supabase.service';

@Component({
  selector: 'app-root',
  imports: [
    LayoutComponent,
    RouterOutlet,
    AsyncPipe,
    HeaderComponent,
    FooterComponent,
    SnackbarHostComponent,
    CookieConsentComponent,
    NotificationToastComponent,
    PushPromptComponent,
    TranslatePipe,
  ],
  templateUrl: './app.component.html',
  styles: [
    `
      /* Sticky footer: app-root is a full-height flex column so the shared
         <app-footer> anchors to the bottom of the viewport on short pages
         (it was display:inline with no height, letting the footer float up). */
      :host {
        display: flex;
        flex-direction: column;
        min-height: 100vh;
        min-height: 100dvh;
      }
      /* The content region grows to fill the space. Signed-out: <main> is a
         direct child; signed-in: <app-layout> wraps it. <main> itself also
         grows so the footer reaches the bottom in the nested signed-in layout. */
      :host > main,
      :host > app-layout {
        flex: 1 0 auto;
      }
      main#main {
        flex: 1 0 auto;
      }

      /* Skip-to-content link: visually hidden until focused (keyboard a11y). */
      .skip-link {
        position: absolute;
        left: 0.5rem;
        top: -3rem;
        z-index: var(--ap-z-skiplink);
        padding: 0.5rem 1rem;
        border-radius: var(--radius, 0.5rem);
        background-color: hsl(var(--card));
        color: hsl(var(--foreground));
        font-weight: 600;
        text-decoration: none;
        box-shadow: 0 2px 8px rgb(0 0 0 / 0.15);
        transition: top 0.15s ease-in-out;
      }
      .skip-link:focus,
      .skip-link:focus-visible {
        top: 0.5rem;
        outline: 2px solid hsl(var(--ring));
        outline-offset: 2px;
      }
      /* Don't show a focus ring on the programmatically-focused main landmark. */
      main:focus {
        outline: none;
      }
      @media (prefers-reduced-motion: reduce) {
        .skip-link {
          transition: none;
        }
      }
    `,
  ],
})
export class AppComponent implements OnInit {
  title = 'actively-angular';
  isLandingPage = false;
  protected user$: Observable<User | null | undefined> | undefined = undefined;

  private destroyRef = inject(DestroyRef);
  private doc = inject<Document>(DOCUMENT);
  private analytics = inject(AnalyticsService);
  private notifications = inject(NotificationsService);

  /** Last identified user id, to avoid duplicate identify/reset churn. */
  private lastUserId: string | null = null;

  constructor(
    private activatedRoute: ActivatedRoute,
    private supabaseService: SupabaseService,
    private router: Router,
    private deepLink: DeepLinkService
  ) {}

  ngOnInit() {
    // Language is preloaded by an app initializer (see app.config) before first paint.

    // Native deep links (Supabase auth callbacks in the Capacitor app). No-op on web.
    this.deepLink.init();

    // The SINGLE router subscription: page-view analytics (consent-gated inside
    // AnalyticsService) + accessibility focus move to the new page on each nav.
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => {
        this.analytics.page(event.urlAfterRedirects);
        this.moveFocusToMain();
      });

    this.activatedRoute.url
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((route) => {
        this.isLandingPage = route[0].path === '';
      });

    this.user$ = this.supabaseService.user$;

    // identify on login / reset on logout (no PII — id only).
    this.supabaseService.user$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => {
        const id = user?.id ?? null;
        if (id && id !== this.lastUserId) {
          this.analytics.identify(id);
          this.lastUserId = id;
          // Bind the in-app notification stream (initial load + realtime).
          void this.notifications.init();
        } else if (!id && this.lastUserId) {
          this.analytics.reset();
          this.lastUserId = null;
          this.notifications.teardown();
        }
      });
  }

  /** Move keyboard focus to the main landmark after a route change (a11y). */
  private moveFocusToMain(): void {
    const main = this.doc.getElementById('main');
    if (main) {
      main.focus({ preventScroll: false });
    }
  }
}
